import ThreeDotsHorizontalIcon from "@redotech/redo-icons/dots-horizontal.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoCommandMenu,
  RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { Flex } from "@redotech/redo-web/flex";
import { calculateSelectionWrapperStyles } from "@redotech/redo-web/selection-wrapper-styles";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo, ReactElement, ReactNode, useMemo, useState } from "react";
import { PartialFlowStep } from "../steps";
import { stepTypes } from "../steps/step-types";
import * as css from "./node.module.css";

export const BaseNode = memo(function BaseNode({
  icon,
  title,
  selected,
  hovered,
  onDelete,
  additionalMenuItems,
  children,
  step,
  containerClassName,
  disabled,
}: {
  icon: ReactElement;
  title: string;
  selected: boolean | undefined;
  hovered?: boolean;
  onDelete?: () => void;
  additionalMenuItems?: RedoCommandMenuItem[];
  children?: ReactNode | ReactNode[];
  step: PartialFlowStep;
  containerClassName?: string;
  disabled?: boolean;
}) {
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  const [actionsDropdownButtonRef, setActionsDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const isValid = step ? (stepTypes[step.type]?.valid(step) ?? false) : false;

  const isSelectable = useMemo(() => {
    if (!step) {
      return false;
    }

    return stepTypes[step.type]?.selectable?.(step) ?? true;
  }, [step]);

  const menuItems: RedoCommandMenuItem[] = useMemo(() => {
    return [
      ...(additionalMenuItems || []),
      ...(onDelete
        ? [
            {
              text: "Delete step",
              onClick: onDelete,
              Icon: TrashIcon,
              theme: "destructive" as const,
            },
          ]
        : []),
    ];
  }, [additionalMenuItems, onDelete]);

  const { wrapperStyle, contentStyle } = calculateSelectionWrapperStyles({
    selected,
    invalid: !isValid,
  });

  return (
    <div
      className={classNames(css.nodeWrapper, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.nodeWrapperSelected]: selected && isValid,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.nodeWrapperInvalid]: !isValid,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.nodeWrapperInvalidSelected]: !isValid && selected,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.nodeWrapperHovered]: hovered && !selected,
      })}
      style={{
        ...wrapperStyle,
        ...(hovered && !selected
          ? {
              outline: "4px solid #ef4444",
              boxShadow: "0 4px 12px rgba(239, 68, 68, 0.4)",
            }
          : {}),
      }}
    >
      <Flex
        className={classNames(
          css.node,
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          { [css.invalid]: !isValid, [css.unselectable]: !isSelectable },
          containerClassName,
        )}
        data-selectable={isSelectable}
        dir="column"
        gap="lg"
        style={contentStyle}
      >
        <Flex justify="space-between">
          <Flex align="center" justify="flex-start">
            {icon}
            <Text fontSize="sm" fontWeight="medium">
              {step?.customTitle || title}
            </Text>
          </Flex>
          {disabled && <RedoBadge text="Inactive" />}
          {menuItems.length > 0 && (
            <>
              <RedoButton
                hierarchy="tertiary"
                IconLeading={ThreeDotsHorizontalIcon}
                onClick={() => setActionsDropdownOpen(true)}
                ref={setActionsDropdownButtonRef}
                size="sm"
              />
              <RedoCommandMenu
                anchor={actionsDropdownButtonRef}
                items={menuItems}
                open={actionsDropdownOpen}
                setOpen={setActionsDropdownOpen}
              />
            </>
          )}
        </Flex>
        {children}
      </Flex>
    </div>
  );
});
